import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { DMHeader } from "./DMHeader";
import { DMMessageList } from "./DMMessageList";
import { DMInput } from "./DMInput";
import { EmojiPicker } from "./EmojiPicker";
import { DM_MESSAGES, getUserById, CURRENT_USER } from "@/mock/data";
import { useChatStore } from "@/store/chatStore";
import { joinRoom, leaveRoom, getSocket } from "@/lib/socket";
import type { Message } from "@snowcord/shared";
import "./DMView.css";

export function DMView() {
  const { userId } = useParams<{ userId: string }>();
  const user = getUserById(userId || "");
  const [isMuted, setIsMuted] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const chatStore = useChatStore();
  const roomId = userId ? `dm:${[CURRENT_USER.id, userId].sort().join(":")}` : "";

  const messages = chatStore.messages.get(roomId) || [];
  const typingUsers = chatStore.typingUsers.get(roomId) || [];

  useEffect(() => {
    if (userId && DM_MESSAGES[userId]) {
      chatStore.setMessages(roomId, DM_MESSAGES[userId]);
    }
  }, [userId, roomId]);

  useEffect(() => {
    if (roomId) {
      joinRoom(roomId);
    }
    return () => {
      if (roomId) {
        leaveRoom(roomId);
      }
    };
  }, [roomId]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const handleNewMessage = (data: { roomId: string; message: Message }) => {
      if (data.roomId === roomId) {
        chatStore.addMessage(data.roomId, data.message);
      }
    };

    const handleEdit = (data: { messageId: string; content: string }) => {
      chatStore.editMessage(roomId, data.messageId, data.content);
    };

    const handleDelete = (data: { messageId: string }) => {
      chatStore.deleteMessage(roomId, data.messageId);
    };

    const handleTypingStart = (data: { roomId: string; userId: string }) => {
      if (data.roomId === roomId) {
        chatStore.setTyping(data.roomId, data.userId, true);
      }
    };

    const handleTypingStop = (data: { roomId: string; userId: string }) => {
      if (data.roomId === roomId) {
        chatStore.setTyping(data.roomId, data.userId, false);
      }
    };

    socket.on("message:new", handleNewMessage);
    socket.on("message:edit", handleEdit);
    socket.on("message:delete", handleDelete);
    socket.on("typing:start", handleTypingStart);
    socket.on("typing:stop", handleTypingStop);

    return () => {
      socket.off("message:new", handleNewMessage);
      socket.off("message:edit", handleEdit);
      socket.off("message:delete", handleDelete);
      socket.off("typing:start", handleTypingStart);
      socket.off("typing:stop", handleTypingStop);
    };
  }, [roomId]);

  const handleSend = useCallback(
    (content: string) => {
      const socket = getSocket();
      if (!socket || !userId) return;

      const tempId = `temp-${Date.now()}`;
      const optimisticMsg: Message = {
        id: tempId,
        senderId: CURRENT_USER.id,
        content,
        nonce: "",
        createdAt: new Date().toISOString(),
        editedAt: null,
        isPinned: false,
        reactions: {},
        status: "pending",
      };

      chatStore.addOptimistic(roomId, tempId, optimisticMsg);

      socket.emit("message:send", {
        receiverId: userId,
        content,
        nonce: crypto.randomUUID(),
      });

      setTimeout(() => {
        chatStore.confirmOptimistic(
          roomId,
          tempId,
          { ...optimisticMsg, id: crypto.randomUUID(), status: "confirmed" },
        );
      }, 300);
    },
    [userId, roomId],
  );

  const handleTypingStart = useCallback(() => {
    const socket = getSocket();
    if (!socket || !userId) return;
    socket.emit("typing:start", { receiverId: userId });
  }, [userId]);

  const handleTypingStop = useCallback(() => {
    const socket = getSocket();
    if (!socket || !userId) return;
    socket.emit("typing:stop", { receiverId: userId });
  }, [userId]);

  if (!user) {
    return (
      <div className="dm-view">
        <div className="dm-view__empty">Select a conversation</div>
      </div>
    );
  }

  const typingNames = typingUsers
    .filter((id) => id !== CURRENT_USER.id)
    .map((id) => getUserById(id)?.displayName || "Someone");

  return (
    <div className="dm-view">
      <DMHeader
        userId={user.id}
        displayName={user.displayName}
        isMuted={isMuted}
        onToggleMute={() => setIsMuted(!isMuted)}
        onStartCall={() => {}}
      />

      <DMMessageList
        messages={messages}
        currentUserId={CURRENT_USER.id}
        getUserDisplayName={(id) => getUserById(id)?.displayName || "Unknown"}
        onLoadMore={() => setLoadingMore(true)}
        hasMore={false}
        loadingMore={loadingMore}
        onReply={() => {}}
        onReact={() => {}}
        onPin={() => {}}
        onEdit={() => {}}
        onDelete={() => {}}
        onRetry={() => {}}
      />

      <DMInput
        placeholder={`Message @${user.displayName}`}
        onSend={handleSend}
        onTypingStart={handleTypingStart}
        onTypingStop={handleTypingStop}
        onEmojiToggle={() => setShowEmojiPicker(!showEmojiPicker)}
      />

      {typingNames.length > 0 && (
        <div style={{ fontSize: 12, color: "var(--muted)", padding: "0 16px" }}>
          {typingNames.join(", ")} {typingNames.length === 1 ? "is" : "are"} typing...
        </div>
      )}

      {showEmojiPicker && (
        <EmojiPicker
          onSelect={(emoji) => {
            setShowEmojiPicker(false);
          }}
          onClose={() => setShowEmojiPicker(false)}
        />
      )}
    </div>
  );
}
